import React, { useMemo, useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Calendar,
  FileText,
  Bell,
  Coffee,
  Camera,
  Edit2,
  X,
  Loader2 as Loader,
  Users,
  Share2,
  BadgeCheck,
  CalendarDays,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useNotes } from '../../contexts/NotesContext';
import { useBreakReminder } from '../../contexts/BreakReminderContext';
import { useCalendar } from '../../contexts/CalendarContext';
import { useChat } from '../../contexts/ChatContext';
import { useNavigate } from 'react-router-dom';
import { authApi, avatarApi } from '../../services/cloudApi';
import AvatarCropModal from '../../components/Common/AvatarCropModal';
import UserAvatar from '../../components/Common/UserAvatar';
import ProfileFriendsModal from '../../components/Profile/ProfileFriendsModal';

const StatCard = ({ icon: Icon, label, value, onClick, clickable = false }) => (
  <div
    role={clickable ? 'button' : undefined}
    tabIndex={clickable ? 0 : undefined}
    onClick={onClick}
    onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); } : undefined}
    className={`rounded-xl border border-white/[0.08] bg-[#161f31] p-4 transition-colors ${
      clickable ? 'cursor-pointer hover:bg-[#1a2640] hover:border-[#3d8ef0]/25' : ''
    }`}
  >
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-[#4d6a8a]" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#4d6a8a]">
        {label}
      </span>
    </div>
    <p className="text-sm font-semibold text-[#e4edf7]">{value}</p>
  </div>
);

const ProfilePage = () => {
  const { t, i18n } = useTranslation('profile');
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, refreshUser, authToken, isPlusUser: authIsPlusUser } = useAuth();
  const { notes } = useNotes();
  const { breaks } = useBreakReminder();
  const calendarContext = useCalendar();
  const { friends, reloadFriends } = useChat();

  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [showAvatarCropModal, setShowAvatarCropModal] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated && authToken) {
      refreshUser(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated && authToken) {
      reloadFriends(true);
    }
  }, [isAuthenticated, authToken, reloadFriends]);

  const events = calendarContext?.events || [];

  const username = useMemo(() => {
    if (!isAuthenticated || !user?.email || !user?.id) return null;
    return '@' + user.email.split('@')[0];
  }, [isAuthenticated, user?.id, user?.email]);

  const membershipType = useMemo(() => {
    if (!isAuthenticated || !user?.id) return 'free';
    let role = user?.role || user?.membershipType || user?.membership_type;
    if (!role || role === 'free') {
      try {
        const storedUser = localStorage.getItem('notesplus_auth_user_v1');
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          role = parsed?.role || parsed?.membershipType || parsed?.membership_type;
        }
      } catch {
        // ignore
      }
    }
    return role || 'free';
  }, [isAuthenticated, user?.id, user?.role, user?.membershipType, user?.membership_type]);

  const roleLabel = useMemo(() => {
    if (membershipType === 'admin') return t('roles.admin');
    if (membershipType === 'plus' || authIsPlusUser) return t('roles.plus');
    return t('roles.standard');
  }, [membershipType, authIsPlusUser, t]);

  const activeFriendsCount = useMemo(
    () => friends.filter((f) => !f.isDeleted).length,
    [friends],
  );

  const collaborativeNotesCount = useMemo(
    () => notes.filter((n) => n.isCollaborative === true).length,
    [notes],
  );

  const calendarReminderCount = useMemo(
    () => events.filter((e) => e.reminderHour || (e.reminderDay && e.reminderDay !== '0')).length,
    [events],
  );

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user || !user?.id) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="w-6 h-6 animate-spin text-[#3d8ef0]" />
      </div>
    );
  }

  if (!isAuthenticated || !user || !user?.id || !user?.email) {
    return null;
  }

  const formatDate = (date) => {
    if (!date) return t('unknown');
    return new Date(date).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleUpdateName = async () => {
    if (!editNameValue.trim() || !authToken || isUpdatingProfile) return;

    setIsUpdatingProfile(true);
    try {
      await authApi.updateProfile({ token: authToken, name: editNameValue.trim() });
      await refreshUser();
      setShowEditNameModal(false);
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('nameUpdatedToast'), message: '' },
      }));
    } catch (error) {
      console.error('Name update error:', error);
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('nameUpdateFailedToast'), message: '' },
      }));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleAvatarSave = async (croppedFile) => {
    if (!authToken) throw new Error('No auth token');

    setIsUpdatingProfile(true);
    try {
      const result = await avatarApi.upload({ token: authToken, file: croppedFile, targetType: 'user' });
      if (!result?.ok) throw new Error(result?.message || t('uploadFailed'));
      await refreshUser();
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: t('avatarUpdatedToast'), message: '' },
      }));
    } catch (error) {
      console.error('Avatar update error:', error);
      let errorMessage = t('avatarUpdateFailedToast');
      if (error.status === 413 || error.message?.includes('413')) {
        errorMessage = t('serverLimitExceeded');
      } else if (error.message) {
        errorMessage = t('errorPrefix', { message: error.message });
      }
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: errorMessage, message: '' },
      }));
      throw error;
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleOpenFriendsModal = async () => {
    setShowFriendsModal(true);
    setFriendsLoading(true);
    try {
      await reloadFriends(true);
    } finally {
      setFriendsLoading(false);
    }
  };

  const displayName = user?.name || user.email?.split('@')[0] || t('defaultUserName');

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[var(--color-bg-base)] h-full overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-[#7a96b0] mb-6">
            {t('subtitle')}
          </p>

          {/* Profil kartı */}
          <div className="rounded-2xl border border-white/[0.08] bg-[#161f31] p-6 mb-6">
            <div className="flex items-center gap-5">
              <div className="relative flex-shrink-0">
                <UserAvatar
                  userId={user?.id}
                  avatarUrl={user?.avatarUrl || user?.avatar_url}
                  name={displayName}
                  size={80}
                  ring
                  className="border-2 border-white/[0.08]"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#0d1424] border border-white/[0.12] text-[#c8d8eb] flex items-center justify-center hover:bg-[#1e2d47] transition-colors"
                  title={t('changePhotoTitle')}
                  disabled={isUpdatingProfile}
                >
                  {isUpdatingProfile ? (
                    <Loader className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file || !authToken) {
                      e.target.value = '';
                      return;
                    }
                    if (file.size > 10 * 1024 * 1024) {
                      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
                        detail: {
                          type: 'error',
                          title: t('fileTooLargeToastTitle', { size: (file.size / (1024 * 1024)).toFixed(2) }),
                          message: t('fileTooLargeToastMessage'),
                        },
                      }));
                      e.target.value = '';
                      return;
                    }
                    setSelectedImageFile(file);
                    setShowAvatarCropModal(true);
                    e.target.value = '';
                  }}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-xl font-bold text-white truncate">{displayName}</h1>
                  <button
                    type="button"
                    onClick={() => {
                      setEditNameValue(user.name || '');
                      setShowEditNameModal(true);
                    }}
                    className="p-1 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.06] transition-colors"
                    title={t('editNameTitle')}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <BadgeCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                </div>
                {username && (
                  <p className="text-sm text-[#4d6a8a] mb-3">{username}</p>
                )}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/[0.06] border border-white/[0.08] text-[#c8d8eb]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4d6a8a]" />
                  {roleLabel}
                </span>
              </div>
            </div>
          </div>

          {/* İstatistik grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            <StatCard icon={Mail} label={t('stats.email')} value={user.email} />
            <StatCard
              icon={Calendar}
              label={t('stats.accountCreated')}
              value={formatDate(user.createdAt || user.created_at)}
            />
            <StatCard
              icon={Users}
              label={t('stats.friends')}
              value={t('stats.friendsCount', { count: activeFriendsCount })}
              clickable
              onClick={handleOpenFriendsModal}
            />
            <StatCard
              icon={Share2}
              label={t('stats.collaboration')}
              value={t('stats.collaborativeNotesCount', { count: collaborativeNotesCount })}
            />
            <StatCard icon={FileText} label={t('stats.totalNotes')} value={t('stats.totalNotesCount', { count: notes.length })} />
            <StatCard
              icon={Bell}
              label={t('stats.totalReminders')}
              value={t('stats.totalRemindersCount', { count: calendarReminderCount })}
            />
            <StatCard
              icon={Coffee}
              label={t('stats.totalBreakReminders')}
              value={t('stats.totalBreakRemindersCount', { count: breaks.length })}
            />
            <StatCard
              icon={CalendarDays}
              label={t('stats.totalEvents')}
              value={t('stats.totalEventsCount', { count: events.length })}
            />
          </div>

        </div>
      </div>

      {/* İsim düzenleme modalı */}
      {showEditNameModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99000] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-2xl w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-white">{t('editNameModal.title')}</h2>
              <button
                type="button"
                onClick={() => setShowEditNameModal(false)}
                className="p-2 rounded-lg hover:bg-white/[0.06] text-[#4d6a8a] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleUpdateName();
                else if (e.key === 'Escape') setShowEditNameModal(false);
              }}
              className="w-full px-4 py-3 bg-[#161f31] border border-white/[0.08] rounded-xl text-white placeholder-[#4d6a8a] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/30 focus:border-[#3d8ef0]/40 transition-all mb-4"
              placeholder={t('editNameModal.placeholder')}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowEditNameModal(false)}
                className="flex-1 px-4 py-2.5 bg-[#161f31] text-[#c8d8eb] rounded-xl hover:bg-[#1e2d47] transition-colors"
              >
                {t('editNameModal.cancel')}
              </button>
              <button
                type="button"
                onClick={handleUpdateName}
                disabled={isUpdatingProfile || !editNameValue.trim()}
                className="flex-1 px-4 py-2.5 bg-[#3d8ef0] hover:bg-[#5ca3f5] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingProfile ? t('editNameModal.saving') : t('editNameModal.save')}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <AvatarCropModal
        isOpen={showAvatarCropModal}
        onClose={() => {
          setShowAvatarCropModal(false);
          setSelectedImageFile(null);
        }}
        imageFile={selectedImageFile}
        onSave={handleAvatarSave}
      />

      <ProfileFriendsModal
        isOpen={showFriendsModal}
        onClose={() => setShowFriendsModal(false)}
        friends={friends}
        loading={friendsLoading}
      />
    </div>
  );
};

export default ProfilePage;
